/**
 * debugReportCapture — console patching, diagnostic collection, submit/download.
 *
 * Hang-proofing (size caps, timeout, downscale) lives in `./debugReportHangProof`
 * (Steffen 2026-04-25 anchor incident + tunable caps). Per-domain snapshot
 * helpers live in `./debugReportCapture.snapshots` and the wire shape in
 * `./debugReportCapture.types`.
 */
import { getPendingCount } from '@appic/lib'
import { enqueue, registerQueueHandler } from '@appic/lib/offline/offlineQueue'
import { shouldQueueFailedWrite } from '@appic/lib/offline/replayability'
import { postActivityLog } from '@appic/lib/utils/scoutLog'
import { readSaveAttempts } from '@appic/lib/http/lastSaveAttemptStore'
import {
  REPORT_SUBMIT_TIMEOUT_MS,
  MAX_SCREENSHOT_BYTES,
  EXPORT_MAX_CONSOLE_ENTRIES,
  EXPORT_MAX_CONSOLE_CHARS,
  estimateDataUrlBytes,
  trimConsoleForExport,
  captureScreenshot,
} from './debugReportHangProof'
import {
  captureAuthSnapshot,
  captureBrowserSnapshot,
  captureCallQueueSnapshot,
  captureIdbQueue,
  captureLocalStorageSnapshot,
  captureRecentActivity,
  captureServiceWorkerSnapshot,
} from './debugReportCapture.snapshots'
import { downloadBundle, tryRescueDownload } from './debugReportCapture.download'
import { buildBugRef, newBugRefNonce } from './bugRef'
import { getBuildInfo } from './buildInfo'
import { installNetworkCapture, getNetworkBuffer, type NetworkEntry } from './debugReportCapture.network'
import { redactDiagnostics } from './debugReportRedact'
import {
  installConsoleCapture,
  getConsoleBuffer,
  filterConsoleNoise,
  installRuntimeErrorCapture,
  getRuntimeErrorBuffer,
} from './debugReportCapture.buffers'
import type {
  ConsoleEntry,
  ConsoleLevel,
  DiagnosticData,
  IdbQueueSummary,
  ReportKind,
  RuntimeErrorEntry,
  RuntimeErrorKind,
} from './debugReportCapture.types'

/**
 * Queue type for a bug-report submit that failed transiently / was made while
 * offline. Enqueued through the SAME offline engine every field-write uses
 * (`enqueue` + `registerQueueHandler` — the primitives `useOfflineMutation`
 * composes), so it replays automatically on reconnect. Kept module-local so
 * all bug-report offline logic lives with the bug-report code.
 */
export const BUG_REPORT_QUEUE_TYPE = 'bug-report:submit'

/** Discriminated submit outcome so the UI can distinguish sent from queued. */
export type SubmitOutcome =
  | { status: 'sent' }
  | { status: 'queued'; pending: number }

// Re-export for back-compat — test files + existing consumers import
// `captureScreenshot` (etc.) from this module. Tests live next to the
// functions; production call sites continue to work unchanged.
export {
  REPORT_SUBMIT_TIMEOUT_MS,
  MAX_SCREENSHOT_BYTES,
  EXPORT_MAX_CONSOLE_ENTRIES,
  EXPORT_MAX_CONSOLE_CHARS,
  estimateDataUrlBytes,
  trimConsoleForExport,
  captureScreenshot,
}
// Console + runtime-error buffers live in `./debugReportCapture.buffers`.
// Re-exported here so tests + existing consumers keep importing them from this
// module unchanged.
export {
  installConsoleCapture,
  getConsoleBuffer,
  filterConsoleNoise,
  installRuntimeErrorCapture,
  getRuntimeErrorBuffer,
}
export type {
  ConsoleEntry,
  ConsoleLevel,
  DiagnosticData,
  IdbQueueSummary,
  NetworkEntry,
  ReportKind,
  RuntimeErrorEntry,
  RuntimeErrorKind,
}
// Network ring buffer lives in `./debugReportCapture.network`. Re-exported so
// tests + consumers import it from this module unchanged.
export { installNetworkCapture, getNetworkBuffer }
// Bundle-to-device egress lives in `./debugReportCapture.download`.
// Re-exported so tests + consumers import it from this module unchanged.
export { downloadBundle }
// GDPR egress redactor — re-exported for tests + any consumer that needs to
// scrub a bundle before shipping it through a different channel.
export { redactDiagnostics } from './debugReportRedact'

// Install the console + runtime-error + network patches at module load
// (idempotent), preserving the original side-effect-on-import behaviour.
installConsoleCapture()
installRuntimeErrorCapture()
installNetworkCapture()

// Register the offline replay handler for queued bug-report submits. Idempotent
// (registerQueueHandler just sets a Map entry); runs at module load — the same
// point the console patches install — so the handler is present before the
// offline queue's reconnect flush fires. Replays the redacted bundle verbatim
// through the real intake event.
registerQueueHandler(BUG_REPORT_QUEUE_TYPE, async (payload) => {
  await postActivityLog(
    'bug_report_submitted',
    payload as Record<string, unknown>,
    { timeoutMs: REPORT_SUBMIT_TIMEOUT_MS },
  )
})

// ── Collector ────────────────────────────────────────────────────────────

export async function collectDiagnostics(
  email: string | null,
  id: string | null,
  kind: ReportKind = 'bug',
): Promise<DiagnosticData> {
  const auth = captureAuthSnapshot()
  const serviceWorker = await captureServiceWorkerSnapshot()
  const recentActivity = await captureRecentActivity(id)
  const queueState = await captureCallQueueSnapshot(recentActivity)
  const idbQueue = await captureIdbQueue()
  const lsData = captureLocalStorageSnapshot()
  const { dataUrl: screenshot, error: screenshotError } = await captureScreenshot()
  const { signal: filteredConsole, noiseCount } = filterConsoleNoise(getConsoleBuffer())
  // Build/version ref — the reliable image attribution (commit-sha collides on
  // rebuilds here, so `build` (Cloud Build id) + `builtAt` are what triage uses).
  const buildInfo = getBuildInfo()
  const buildHash = buildInfo.commit  // back-compat: legacy `buildHash` field == git short sha
  // SL base URL the FE actually calls. Same accessor every SL client uses
  // (VITE_SERVICE_LAYER_BASE_URL, default '/service'); typed env read so a
  // missing import.meta.env in a test runtime degrades to the default.
  const slBaseUrl = ((import.meta as ImportMeta & { env?: Record<string, string | undefined> })
    .env?.VITE_SERVICE_LAYER_BASE_URL) || '/service'
  const appOrigin = (typeof window !== 'undefined' && window.location?.origin) || 'unknown'

  // Bug-ref MUST be derived from the same `timestamp` we put on the bundle,
  // so the ref the user reads back to support matches the row the on-duty
  // agent lands. Compute it once, here, before the object literal.
  const timestamp = new Date().toISOString()
  const bugRef = await buildBugRef({ userId: id, timestamp, nonce: newBugRefNonce(), kind })
    .catch(() => null)   // never block the bundle on a digest hiccup — UI degrades to "ref unavailable"

  const bundle: DiagnosticData = {
    timestamp,
    kind,
    bugRef,
    userComment: null,   // Caller (UI) attaches the user's comment via `withComment` before submit.
    user: { email, id },
    browser: captureBrowserSnapshot(),
    route: window.location.pathname + window.location.search,
    env: {
      commit: buildInfo.commit,
      build: buildInfo.build,
      builtAt: buildInfo.builtAt,
      appOrigin,
      slBaseUrl,
    },
    auth,
    serviceWorker,
    localStorage: lsData,
    recentActivity, idbQueue, queueState, lastSaveAttempts: readSaveAttempts(),
    console: filteredConsole, consoleNoiseFiltered: noiseCount,
    runtimeErrors: getRuntimeErrorBuffer(),
    networkRequests: getNetworkBuffer(),
    screenshot, screenshotError, buildHash,
  }

  // GDPR: redact at collect time so the PREVIEW the user sees is exactly what
  // will be sent (what-you-see-is-what-ships). Redaction is idempotent, so
  // submit/download redact again to also catch the comment attached later.
  return redactDiagnostics(bundle)
}

// ── Submit + download ────────────────────────────────────────────────────

/** Normalise Supabase PostgrestError / any error-ish object into a real Error. */
function describeError(e: unknown): Error {
  if (e instanceof Error) return e
  if (e && typeof e === 'object') {
    const o = e as Record<string, unknown>
    const parts = [o.code, o.message, o.details, o.hint]
      .filter((p): p is string => typeof p === 'string' && p.length > 0)
    const err = new Error(parts.length > 0 ? parts.join(' — ') : JSON.stringify(o))
    ;(err as Error & { raw?: unknown }).raw = e
    return err
  }
  return new Error(String(e))
}

export async function submitDiagnostics(report: DiagnosticData): Promise<SubmitOutcome> {
  if (!report.user.id) throw new Error('No user id — download the bundle and share it via another channel')
  // Redact + trim at the egress boundary. `redactDiagnostics` is idempotent
  // (collect already redacted the bundle) but re-running here guarantees the
  // comment attached after collect is scrubbed too. GDPR: nothing leaves the
  // device un-redacted.
  const submitReport = redactDiagnostics({ ...report, console: trimConsoleForExport(report.console) })
  // Diagnostic garnish for the failure messages below. A non-serializable
  // bundle must not mask the REAL submit failure — report its size honestly
  // as unmeasurable instead of a fabricated 0.
  const payloadBytes: number | 'unmeasurable' = (() => {
    try {
      return JSON.stringify(submitReport).length
    } catch {
      return 'unmeasurable'
    }
  })()
  // Event name `bug_report_submitted` is the contract the on-duty agent
  // watches to land a manifest row keyed on `payload.bugRef` and emit the
  // reply signal back to the duty channel
  // (FR-FE-BUG-REPORT-UNIQUE-REF-SHOWN-TO-USER-2026-05-24, mechanics 2+3).
  // Renamed from `debug_report_submitted`; the previous name was diagnostic-
  // bundle-shaped and didn't trigger intake — keep this aligned with the
  // server-side intake handler.
  //
  // FEATURE REQUESTS reuse this SAME event by design (no backend change):
  // they ship `payload.kind === 'feature'` + an `FR-` ref. The row-watcher
  // still fires (so nothing is dropped); the triage agent reads
  // `payload.kind` to route feature requests to the backlog instead of the
  // bug lane (FR-FE-BUG-TOOL-FEATURE-REQUEST-FLOW-2026-05-24).
  // FAILURE HANDLING (never silent — D-33 R2):
  //   1. Transient / offline / auth-blip / server 5xx (`shouldQueueFailedWrite`
  //      — the SAME SSoT `useOfflineMutation` uses): ENQUEUE the redacted bundle
  //      for automatic replay on reconnect and return { status:'queued' }. The
  //      UI shows a distinct "queued, will send when back online" confirmation
  //      (still surfacing the bugRef), NOT a scary error and NOT a false "sent".
  //   2. Non-replayable hard failure (400/422), OR the enqueue itself failing:
  //      write the full bundle to device and re-throw LOUDLY so the dialog's
  //      error state + manual Download button fire.
  try {
    await postActivityLog(
      'bug_report_submitted',
      submitReport as unknown as Record<string, unknown>,
      { timeoutMs: REPORT_SUBMIT_TIMEOUT_MS },
    )
    return { status: 'sent' }
  } catch (err) {
    if (shouldQueueFailedWrite(err)) {
      try {
        await enqueue(BUG_REPORT_QUEUE_TYPE, submitReport as unknown as Record<string, unknown>)
        const pending = await getPendingCount()
        return { status: 'queued', pending }
      } catch (enqueueErr) {
        // Local persist itself failed — the ONLY case that must fail loud even
        // for a transient network error (FR-FE-OFFLINE-FIRST §I). Fall through
        // to the download + throw below so nothing is lost.
        const bundleDownloaded = tryRescueDownload(report)
        const wrapped = describeError(enqueueErr)
        wrapped.message = `${wrapped.message} [offline_enqueue_failed, payload_bytes=${payloadBytes}, bundle_downloaded=${bundleDownloaded}]`
        throw wrapped
      }
    }
    const bundleDownloaded = tryRescueDownload(report)
    const wrapped = describeError(err)
    wrapped.message = `${wrapped.message} [payload_bytes=${payloadBytes}, bundle_downloaded=${bundleDownloaded}]`
    throw wrapped
  }
}

// Bundle-to-device egress (rescue download + manual download) lives in
// `./debugReportCapture.download`. `downloadBundle` is re-exported above so
// tests + existing consumers keep importing it from this module unchanged.
